import type { Rng } from '../core/rng/Rng';

/**
 * Shared map types + carving/query primitives. The legacy serpentine
 * brush-walk generator that used to live here was retired at the MAPGEN_PLAN
 * P4 flip — chunk-stitched floors (`ChunkGen.ts`) are the game now. What
 * remains is the contract surface both generators shared:
 *   - `GeneratedMap`/`Room`/`TilePos` — the shape every consumer reads
 *   - carve primitives — VaultGen still sculpts the Reliquary with them
 *   - `bfsDistances`/`nearestFloor` — used by ChunkGen, VaultGen, and World
 */

export const TILE = 32;

/** Biome identity carried by every map — drives the render theme (ME1). */
export type MapArchetype = 'mixed' | 'warrens' | 'halls' | 'sanctum' | 'vault';

export interface Room {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

export interface TilePos {
  x: number;
  y: number;
}

export interface GeneratedMap {
  w: number;
  h: number;
  /** Biome for theming; generators always set it ('vault' for VaultGen). */
  archetype: MapArchetype;
  /** 0 = wall, 1 = floor. */
  tiles: Uint8Array;
  rooms: Room[];
  entrance: TilePos;
  exit: TilePos;
  /** Near the entrance, used on floors 2+. */
  shop: TilePos;
  /** Summoning-circle positions, deepest first. */
  circleCandidates: TilePos[];
  /** Rooms enemies may spawn in (everything but the entrance chamber). */
  spawnRegions: Room[];
  /** Dead-end pocket rooms (subset of rooms). */
  pockets: Room[];
  /**
   * Draw-free event anchor tiles; floor events (and the vault door) are
   * placed onto these in order. Slot 0 is deepest (the vault door lives in
   * a dead end); later slots are ordered for spatial spread.
   */
  eventCandidates: TilePos[];
}

// ---------------------------------------------------------------------------
// Carving primitives

export function stampCircle(tiles: Uint8Array, w: number, h: number, px: number, py: number, r: number): void {
  const minX = Math.max(1, Math.floor(px - r));
  const maxX = Math.min(w - 2, Math.ceil(px + r));
  const minY = Math.max(1, Math.floor(py - r));
  const maxY = Math.min(h - 2, Math.ceil(py + r));
  const r2 = r * r;
  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      const dx = tx - px;
      const dy = ty - py;
      if (dx * dx + dy * dy <= r2) tiles[ty * w + tx] = 1;
    }
  }
}

/** Walk a→b in 1-tile steps, stamping a jittered wide brush each step. */
export function carveBrushWalk(
  tiles: Uint8Array, w: number, h: number,
  a: { x: number; y: number }, b: { x: number; y: number },
  rng: Rng, rMin: number, rMax: number, jitterMax: number,
): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const steps = Math.ceil(len);
  const perpX = -dy / len;
  const perpY = dx / len;
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const r = rng.range(rMin, rMax);
    const jit = rng.range(-jitterMax, jitterMax);
    stampCircle(tiles, w, h, a.x + dx * t + perpX * jit, a.y + dy * t + perpY * jit, r);
  }
}

/** Ellipse with two sine harmonics on the rim — reads as a cavern, not a room. */
export function carveWobbledEllipse(
  tiles: Uint8Array, w: number, h: number,
  cx: number, cy: number, rx: number, ry: number,
  a1: number, a2: number, phi1: number, phi2: number,
): void {
  const extX = Math.ceil(rx * (1 + a1 + a2)) + 1;
  const extY = Math.ceil(ry * (1 + a1 + a2)) + 1;
  const minX = Math.max(1, cx - extX);
  const maxX = Math.min(w - 2, cx + extX);
  const minY = Math.max(1, cy - extY);
  const maxY = Math.min(h - 2, cy + extY);
  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      const nx = (tx - cx) / rx;
      const ny = (ty - cy) / ry;
      const rad = Math.hypot(nx, ny);
      if (rad === 0) {
        tiles[ty * w + tx] = 1;
        continue;
      }
      const theta = Math.atan2(ny, nx);
      const m = 1 + a1 * Math.sin(3 * theta + phi1) + a2 * Math.sin(5 * theta + phi2);
      if (rad <= m) tiles[ty * w + tx] = 1;
    }
  }
}

// ---------------------------------------------------------------------------
// Queries

/** Spiral outward from (sx, sy) to the nearest floor tile. */
export function nearestFloor(tiles: Uint8Array, w: number, h: number, sx: number, sy: number): TilePos {
  for (let r = 0; r < Math.max(w, h); r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = sx + dx;
        const y = sy + dy;
        if (x >= 0 && x < w && y >= 0 && y < h && tiles[y * w + x] === 1) {
          return { x, y };
        }
      }
    }
  }
  throw new Error('no floor tile found');
}

/** BFS over floor tiles; -1 = unreachable. */
export function bfsDistances(tiles: Uint8Array, w: number, h: number, sx: number, sy: number): Int32Array {
  const dist = new Int32Array(w * h).fill(-1);
  const queue = new Int32Array(w * h);
  let head = 0;
  let tail = 0;
  const start = sy * w + sx;
  dist[start] = 0;
  queue[tail++] = start;
  while (head < tail) {
    const cur = queue[head++]!;
    const cx = cur % w;
    const cy = (cur / w) | 0;
    const d = dist[cur]!;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
      const ni = ny * w + nx;
      if (tiles[ni] === 1 && dist[ni] === -1) {
        dist[ni] = d + 1;
        queue[tail++] = ni;
      }
    }
  }
  return dist;
}
